//@ts-check
import adaptiveApi from 'api/adaptiveAiApi';
import useFetch from 'hooks/use-fetch';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-form';
import { Form } from 'reactstrap';
import classNames from 'classnames';
import { CheckCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Col, Row, Skeleton } from 'antd';
import Swal from 'sweetalert2';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import { QUESTION_TYPE, UNIT_STATUS } from 'constants/constants';
import { handleToastError, stripHtml } from 'helpers/index';
import { completeUnitAsync, fetchUnitAsync } from 'store/units';
import { getSelectedCentre } from 'store/master';
import { showToastSuccess } from '@core/components/toastify/ToastTypes';
import { getThemeColor } from 'store/settings';
import {
  DragAndDrop,
  FillInTheBlank,
  FillTheGap,
  FreeText,
  MultipleChoice,
  Ordering,
} from 'modules/Course/Unit/components/QuestionTypev2';
import MicroLearningView from './MicroLearningView';

const EMPTY_FORM_DEFAULTS = {
  multipleChoice: [],
  fillInTheBlank: [],
  fillTheGap: [],
  ordering: [],
  freeText: '',
  dragDrop: [],
};

function getFormDataForQuestion(questionType, value) {
  const hasResultValue = value?.result?.length;
  let formValue;
  if (hasResultValue) {
    formValue = value?.result;
  } else {
    formValue = value?.data;
  }
  switch (questionType) {
    case QUESTION_TYPE.MTC:
      return {
        value: value?.result,
        label: 'multipleChoice',
      };
    case QUESTION_TYPE.FILLINTHEBLANK:
      return {
        value: value?.result,
        label: 'fillInTheBlank',
      };
    case QUESTION_TYPE.FILLTHEGAP:
      return {
        value: value?.result,
        label: 'fillTheGap',
      };
    case QUESTION_TYPE.ORDERING: {
      const sourceData = hasResultValue
        ? formValue
        : value?.forOrdering || value?.data;
      return {
        value:
          sourceData?.map((item) => ({
            id: crypto.randomUUID(),
            content: item?.content || item,
          })) ?? [],
        label: 'ordering',
      };
    }
    case QUESTION_TYPE.DRAGDROP: {
      const sourceData = hasResultValue
        ? formValue
        : value?.forDragDrop || value?.data;
      return {
        value:
          sourceData?.map((item) => ({
            id: crypto.randomUUID(),
            content: item?.correct || item?.content || item,
          })) ?? [],
        label: 'dragDrop',
      };
    }
    case QUESTION_TYPE.FREETEXT:
      return {
        value: value?.dataContent ?? '',
        label: 'freeText',
      };
    default:
      return {
        value: [],
        label: '',
      };
  }
}

function buildAnswerPayload(formData, questionType) {
  switch (questionType) {
    case QUESTION_TYPE.MTC:
      return formData.multipleChoice;
    case QUESTION_TYPE.FILLINTHEBLANK:
      return formData.fillInTheBlank;
    case QUESTION_TYPE.FILLTHEGAP:
      return formData.fillTheGap;
    case QUESTION_TYPE.ORDERING:
      return formData.ordering?.map((item) => item.content);
    case QUESTION_TYPE.DRAGDROP:
      return formData.dragDrop?.map((item) => item.content);
    case QUESTION_TYPE.FREETEXT:
      return formData.freeText ?? '';
    default:
      return null;
  }
}

function reFactorQuestion(list, activeQuestionId) {
  const hasLength = !!list.length;
  let row = null;
  if (activeQuestionId) {
    row =
      list.find((item) => item.question?._id === activeQuestionId) ?? list[0];
  } else if (hasLength) {
    row = list[0];
  }
  return {
    ...(row?.userQuestion || row),
    forOrdering:
      row?.forOrdering ||
      row?.question?.answers?.map((item) => item?.content ?? item),
    forDragDrop:
      row?.forDragDrop ||
      row?.question?.corrects?.map((item) => item?.content ?? item),
    questionNumber: row?.question?.questionNumber ?? row?.questionNumber ?? 1,
    questionId: row?.question?._id ?? row?.questionId ?? null,
  };
}

function escapeHtmlForSwal(text) {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** When submit returns path changes, show why lessons were skipped or added. */
async function showAdaptiveLessonPathFeedback(result, themeColor) {
  const skipList = result?.skipLesson ?? [];
  const suppList = result?.supplementaryLesson ?? [];
  if (skipList.length === 0 && suppList.length === 0) return;

  const parts = [];
  if (skipList.length > 0) {
    parts.push(
      '<p class="text-left mb-2" style="color:#374151;line-height:1.5">' +
        'These lessons are skipped based on your adaptive results so your path stays focused on what you still need.' +
        '</p>',
      '<ul class="text-left mb-3 pl-3" style="color:#111827;margin-top:0">' +
        skipList
          .map((item) => `<li>${escapeHtmlForSwal(item?.title)}</li>`)
          .join('') +
        '</ul>',
    );
  }
  if (suppList.length > 0) {
    parts.push(
      '<p class="text-left mb-2" style="color:#374151;line-height:1.5">' +
        'These supplementary lessons were added to give you extra practice where it will help most.' +
        '</p>',
      '<ul class="text-left mb-0 pl-3" style="color:#111827;margin-top:0">' +
        suppList
          .map((item) => `<li>${escapeHtmlForSwal(item?.title)}</li>`)
          .join('') +
        '</ul>',
    );
  }

  await Swal.fire({
    icon: 'info',
    title: 'Your lesson path was updated',
    html: parts.join(''),
    confirmButtonText: 'Continue',
    confirmButtonColor: themeColor || 'primary',
    allowOutsideClick: true,
    focusConfirm: true,
  });
}

const ADAPTIVE_UI = {
  pageBg: 'linear-gradient(135deg, #f5f3ff 0%, #ffffff 50%, #eff6ff 100%)',
  cardShadow: '0 10px 40px rgba(17, 24, 39, 0.08)',
  cardBorder: '1px solid rgba(229, 231, 235, 0.8)',
  cardRadius: 24,
  innerMaxWidth: 780,
  track: '#eef2f7',
  mutedText: '#6b7280',
  titleText: '#111827',
};

function QuestionAnswerSkeleton() {
  return (
    <div
      className="adaptive-question-shell"
      style={{
        background: ADAPTIVE_UI.pageBg,
        borderRadius: ADAPTIVE_UI.cardRadius,
        padding: '28px 16px',
      }}
    >
      <div
        style={{
          maxWidth: ADAPTIVE_UI.innerMaxWidth,
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: ADAPTIVE_UI.cardRadius,
          boxShadow: ADAPTIVE_UI.cardShadow,
          border: ADAPTIVE_UI.cardBorder,
          padding: '28px 28px 24px',
        }}
      >
        <Skeleton
          active
          paragraph={{ rows: 3, width: ['100%', '85%', '60%'] }}
          title={{ width: '45%', style: { marginTop: 2 } }}
          className="mb-3"
        />
      </div>
    </div>
  );
}

function QuestionList({
  unitId,
  totalQuestions,
  unitStatus = undefined,
  microLearningContent,
}) {
  const dispatch = useDispatch();
  const themeColor = useSelector(getThemeColor);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [isRestarting, setIsRestarting] = useState(false);

  const questionType = currentQuestion?.type;
  const questionNumber = currentQuestion?.questionNumber;

  useEffect(() => {
    if (!microLearningContent || unitStatus !== UNIT_STATUS.FAILED || !unitId) {
      return undefined;
    }
    // const key = `${MICRO_LEARNING_EXPLAINER_STORAGE_KEY}:${unitId}`;
    // if (typeof sessionStorage !== 'undefined') {
    //   if (sessionStorage.getItem(key)) {
    //     return undefined;
    //   }
    //   sessionStorage.setItem(key, '1');
    // }

    Swal.fire({
      icon: 'info',
      title: 'Micro-learning assigned',
      html:
        '<p class="text-left mb-0" style="color:#374151;line-height:1.5">' +
        'Your last attempt did not meet the pass requirement for this adaptive test. ' +
        'Micro-learning has been assigned so you can review the key ideas and build confidence before you try again. ' +
        'When you are ready, use <strong>Start</strong> below to begin a new attempt.' +
        '</p>',
      confirmButtonText: 'Continue',
      confirmButtonColor: themeColor,
      allowOutsideClick: true,
      focusConfirm: true,
    });

    return () => {
      Swal.close();
    };
  }, [microLearningContent, unitStatus, unitId]);

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    defaultValues: EMPTY_FORM_DEFAULTS,
    shouldUnregister: false,
  });

  const { loading: isInitialLoading, error: fetchError } = useFetch({
    queryKey: [
      'adaptive-ai-unit-question',
      unitId,
      currentQuestionId,
      unitStatus,
    ],
    queryFn: () => adaptiveApi.getAllAiQuestion(unitId),
    enable: !!unitId,
    onSuccess: (response) => {
      const list = Array.isArray(response) ? response : [response];

      const question = reFactorQuestion(list, currentQuestionId);
      console.log(question, 'question');
      if (list.length > 0) {
        setCurrentQuestion(question);
        if (question?.questionId) {
          localStorage.setItem('currentQuestionId', question.questionId);
        }
      } else {
        setCurrentQuestion(null);
      }
    },
  });

  useEffect(() => {
    if (fetchError) handleToastError(fetchError);
  }, [fetchError]);

  useEffect(() => {
    if (currentQuestion) {
      const { label, value } = getFormDataForQuestion(
        questionType,
        currentQuestion,
      );
      setValue(label, value);
    }
  }, [currentQuestion]);

  const cssClass = useMemo(() => {
    const t = questionType;
    return classNames('d-flex fill-overflow-image w-100', {
      fillinthegapstyle: t === QUESTION_TYPE.FILLTHEGAP,
      'align-items-baseline': t === QUESTION_TYPE.FILLINTHEBLANK,
    });
  }, [questionType]);

  const onSubmit = async (formData, navigation = 'next') => {
    const payload = buildAnswerPayload(formData, questionType);

    try {
      const body = {
        data: payload,
        id: currentQuestion?._id,
        isEmpty: false,
      };

      const typesUsingArrayPayload = [
        QUESTION_TYPE.FILLINTHEBLANK,
        QUESTION_TYPE.FILLTHEGAP,
        QUESTION_TYPE.ORDERING,
        QUESTION_TYPE.DRAGDROP,
        QUESTION_TYPE.MTC,
      ];
      if (
        typesUsingArrayPayload.includes(questionType) &&
        Array.isArray(payload) &&
        payload.length === 0
      ) {
        body.isEmpty = true;
      } else if (questionType === QUESTION_TYPE.FREETEXT && !payload) {
        body.isEmpty = true;
      }

      if (navigation === 'submit') {
        const response = await adaptiveApi.getSubmit(
          unitId,
          currentQuestion?.questionId,
          body,
        );
        if (response?.result) {
          await showAdaptiveLessonPathFeedback(response?.result, themeColor);
          await fetchUnitAsync(unitId)(dispatch);
          showToastSuccess('Adaptive test submitted successfully');
        }
      } else if (navigation === 'next' || navigation === 'prev') {
        const nextIndex =
          navigation === 'next'
            ? Math.min(currentQuestion?.questionNumber + 1, totalQuestions)
            : Math.max(currentQuestion?.questionNumber - 1, 1);

        const updateResult = await adaptiveApi.updateApi(body, {
          unitId,
          questionId: currentQuestion?.questionId,
          questionIndex: currentQuestion?.questionNumber,
          nextIndex,
        });
        if (updateResult?.question?._id) {
          setCurrentQuestionId(updateResult?.question?._id);
          reset({});
        }
      }
    } catch (error) {
      handleToastError(error);
    }
  };

  const goPrev = () => {
    handleSubmit((formData) => onSubmit(formData, 'prev'))();
  };

  const goNext = () => {
    handleSubmit((formData) => onSubmit(formData, 'next'))();
  };

  const handleRestartAdaptiveTest = async () => {
    if (!unitId) return;
    try {
      setIsRestarting(true);

      const response = await adaptiveApi.startAdaptiveAiUnitQuestion(unitId);
      await fetchUnitAsync(unitId)(dispatch);
      if (response?._id) {
        showToastSuccess('Adaptive learning started successfully');
      }
      localStorage.removeItem('currentQuestionId');
      setCurrentQuestionId(null);
      reset(EMPTY_FORM_DEFAULTS);
    } catch (error) {
      handleToastError(error);
    } finally {
      setIsRestarting(false);
    }
  };

  const renderInfoState = (message) => (
    <div
      style={{
        background: ADAPTIVE_UI.pageBg,
        borderRadius: ADAPTIVE_UI.cardRadius,
        padding: '28px 16px',
      }}
    >
      <div
        style={{
          maxWidth: ADAPTIVE_UI.innerMaxWidth,
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: ADAPTIVE_UI.cardRadius,
          boxShadow: ADAPTIVE_UI.cardShadow,
          border: ADAPTIVE_UI.cardBorder,
          padding: '32px',
          textAlign: 'center',
          color: ADAPTIVE_UI.mutedText,
        }}
      >
        {message}
      </div>
    </div>
  );

  if (!unitId) {
    return renderInfoState('No unit selected.');
  }

  if (microLearningContent && unitStatus === UNIT_STATUS.FAILED) {
    return (
      <div
        style={{
          background: ADAPTIVE_UI.pageBg,
          borderRadius: ADAPTIVE_UI.cardRadius,
          padding: '28px 16px',
        }}
      >
        <div
          style={{
            maxWidth: ADAPTIVE_UI.innerMaxWidth,
            margin: '0 auto',
            background: '#ffffff',
            borderRadius: ADAPTIVE_UI.cardRadius,
            boxShadow: ADAPTIVE_UI.cardShadow,
            border: ADAPTIVE_UI.cardBorder,
            padding: '28px 28px 24px',
          }}
        >
          <MicroLearningView microLearningContent={microLearningContent}>
            <div className="d-flex justify-content-center mt-4">
              <ThemeButton
                type="button"
                padding="10px 28px"
                className="rounded-pill"
                onClick={handleRestartAdaptiveTest}
                isLoading={isRestarting}
              >
                Start
              </ThemeButton>
            </div>
          </MicroLearningView>
        </div>
      </div>
    );
  }

  if (isInitialLoading) {
    return <QuestionAnswerSkeleton />;
  }

  if (!currentQuestion) {
    return renderInfoState('No question loaded. Try refreshing the page.');
  }

  const progressPct =
    totalQuestions > 0
      ? Math.min(100, Math.max(0, (questionNumber / totalQuestions) * 100))
      : 0;

  const progressFillColor = themeColor || '#7c3aed';

  return (
    <div
      className="adaptive-question-shell"
      style={{
        background: ADAPTIVE_UI.pageBg,
        borderRadius: ADAPTIVE_UI.cardRadius,
        padding: '28px 16px',
      }}
    >
      {/* Header with progress */}
      <div
        style={{
          maxWidth: ADAPTIVE_UI.innerMaxWidth,
          margin: '0 auto 20px',
        }}
      >
        <div
          style={{
            height: 10,
            background: ADAPTIVE_UI.track,
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              background: progressFillColor,
              borderRadius: 999,
              transition: 'width 300ms ease',
            }}
          />
        </div>
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 13,
            color: ADAPTIVE_UI.mutedText,
            fontWeight: 500,
          }}
        >
          Question {questionNumber} of {totalQuestions}
        </p>
      </div>

      {/* Question Card */}
      <Form
        onSubmit={(e) => e.preventDefault()}
        className="require-label adaptive-question-list"
        style={{
          maxWidth: ADAPTIVE_UI.innerMaxWidth,
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: ADAPTIVE_UI.cardRadius,
          boxShadow: ADAPTIVE_UI.cardShadow,
          border: ADAPTIVE_UI.cardBorder,
          padding: '32px 32px 24px',
        }}
      >
        <Row className={cssClass} wrap>
          <Col span={24}>
            <p
              className="d-flex"
              style={{
                fontSize: 20,
                lineHeight: 1.6,
                color: ADAPTIVE_UI.titleText,
                fontWeight: 500,
                marginBottom: 28,
              }}
            >
              <span
                className="mr-2 question-text"
                style={{
                  fontWeight: 700,
                  color: progressFillColor,
                  flexShrink: 0,
                }}
              >{`${questionNumber}.`}</span>
              {questionType !== QUESTION_TYPE.FILLINTHEBLANK &&
                questionType !== QUESTION_TYPE.FILLTHEGAP && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: currentQuestion?.content,
                    }}
                  />
                )}
              {questionType === QUESTION_TYPE.FILLINTHEBLANK && (
                <Controller
                  name="fillInTheBlank"
                  control={control}
                  render={({ field }) => (
                    <FillInTheBlank
                      contentHTML={
                        currentQuestion?.contentHTML ||
                        currentQuestion?.modifiedContent
                      }
                      className="ml-1 text-word-break fill-blank w-100 fill-overflow-image d-flex flex-wrap"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
              {questionType === QUESTION_TYPE.FILLTHEGAP && (
                <Controller
                  name="fillTheGap"
                  control={control}
                  render={({ field }) => (
                    <FillTheGap
                      contentHTML={
                        currentQuestion?.contentHTML ||
                        currentQuestion?.modifiedContent
                      }
                      className="ml-1 text-word-break fill-blank w-100 fill-overflow-image d-flex flex-wrap"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </p>

            {questionType === QUESTION_TYPE.ORDERING && (
              <Controller
                name="ordering"
                control={control}
                render={({ field }) => (
                  <Ordering value={field.value} onChange={field.onChange} />
                )}
              />
            )}

            {questionType === QUESTION_TYPE.FREETEXT && (
              <Controller
                name="freeText"
                control={control}
                render={({ field }) => (
                  <FreeText
                    isFullscreenMode={false}
                    onChange={field.onChange}
                    value={field.value}
                    explanation={currentQuestion.explanation}
                  />
                )}
              />
            )}

            {questionType === QUESTION_TYPE.DRAGDROP && (
              <Controller
                name="dragDrop"
                control={control}
                render={({ field }) => (
                  <DragAndDrop
                    onChange={field.onChange}
                    questions={currentQuestion.data?.map(
                      (item) => item.content,
                    )}
                    value={field.value}
                  />
                )}
              />
            )}

            {questionType === QUESTION_TYPE.MTC && (
              <Controller
                name="multipleChoice"
                control={control}
                render={({ field }) => (
                  <MultipleChoice
                    question={currentQuestion.data ?? []}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </Col>

          <Col
            span={24}
            className="d-flex align-items-center justify-content-between"
            style={{
              marginTop: 28,
              paddingTop: 20,
              borderTop: '1px solid #eef2f7',
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              {questionNumber !== 1 && (
                <ThemeButton
                  padding="10px 24px"
                  className="rounded-pill"
                  variant="secondary"
                  type="button"
                  disabled={isSubmitting}
                  onClick={goPrev}
                >
                  Previous
                </ThemeButton>
              )}
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: ADAPTIVE_UI.mutedText,
                  letterSpacing: 0.2,
                }}
              >
                {questionNumber} / {totalQuestions}
              </span>
            </div>

            <div
              style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}
            >
              {questionNumber < totalQuestions && (
                <ThemeButton
                  padding="10px 24px"
                  className="rounded-pill"
                  type="button"
                  disabled={isSubmitting}
                  onClick={goNext}
                >
                  Next
                </ThemeButton>
              )}
              {questionNumber === totalQuestions && (
                <ThemeButton
                  type="button"
                  padding="10px 28px"
                  className="rounded-pill"
                  disabled={isSubmitting}
                  onClick={() =>
                    handleSubmit((formData) => onSubmit(formData, 'submit'))()
                  }
                >
                  Submit
                </ThemeButton>
              )}
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
}

export default QuestionList;
